import React, { useEffect, useId, useRef, useState } from "react";

import type { Locale } from "../content/types";

type DynamicMailSamlWidgetMount = {
  unmount: () => void;
};

type DynamicMailSamlWidgetApi = {
  mount: (
    target: Element,
    options: {
      startUrl: string;
      completeUrl: string;
      apiBase: string;
      bankIdLogoUrl?: string;
      onComplete: (data: Record<string, unknown>) => void;
      onError: (error: unknown) => void;
    }
  ) => DynamicMailSamlWidgetMount;
};

declare global {
  interface Window {
    DynamicMailSamlWidget?: DynamicMailSamlWidgetApi;
  }
}

type SamlWidgetLoginProps = {
  locale: Locale;
  fallbackHref: string;
  label: string;
  className?: string;
};

const widgetLoaderUrl = "https://www.dynamicmail.ai/saml/widget/v1/dynamicbooks.js?v=20260516-polling";
const widgetApiBase = "https://www.dynamicmail.ai";
const bankIdLogoUrl = "https://www.dynamicmail.ai/admin/dynamic-mail/assets/BankID_logo_white.png";
const completeUrl = "/auth/saml/widget/complete";
const mailLandingUrl = "https://mail.dynamicmail.ai/admin/inbox#open_modal=dynamicbooks";

const copy: Record<Locale, {
  title: string;
  lead: string;
  close: string;
  fallback: string;
  loading: string;
  failed: string;
}> = {
  sv: {
    title: "Logga in i DynamicBooks",
    lead: "Välj BankID eller Google.",
    close: "Stäng",
    fallback: "Fortsätt med helsidesinloggning",
    loading: "Laddar inloggning...",
    failed: "Inloggningen kunde inte laddas."
  },
  en: {
    title: "Log in to DynamicBooks",
    lead: "Choose BankID or Google.",
    close: "Close",
    fallback: "Continue with full-page login",
    loading: "Loading sign in...",
    failed: "Sign in could not be loaded."
  }
};

let widgetScriptPromise: Promise<void> | null = null;

export function SamlWidgetLogin({ locale, fallbackHref, label, className }: SamlWidgetLoginProps) {
  const text = copy[locale];
  const titleId = useId();
  const descriptionId = useId();
  const triggerRef = useRef<HTMLAnchorElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const mountRef = useRef<HTMLDivElement | null>(null);
  const hasOpenedModalRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    window.setTimeout(() => focusWithoutScrolling(closeRef.current), 0);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      if (hasOpenedModalRef.current) {
        focusWithoutScrolling(triggerRef.current);
      }
      return;
    }

    let disposed = false;
    let mountedWidget: DynamicMailSamlWidgetMount | null = null;
    setStatus("loading");

    loadWidgetScript()
      .then(() => {
        if (disposed || !mountRef.current) {
          return;
        }
        const widget = window.DynamicMailSamlWidget;
        if (!widget?.mount) {
          throw new Error("widget_missing");
        }
        mountedWidget = widget.mount(mountRef.current, {
          startUrl: buildStartUrl(fallbackHref),
          completeUrl,
          apiBase: widgetApiBase,
          bankIdLogoUrl,
          onComplete(data) {
            window.location.assign(resolveMailLandingUrl(data));
          },
          onError() {
            if (!disposed) {
              setStatus("error");
            }
          }
        });
        setStatus("ready");
      })
      .catch(() => {
        if (!disposed) {
          setStatus("error");
        }
      });

    return () => {
      disposed = true;
      mountedWidget?.unmount();
    };
  }, [fallbackHref, isOpen]);

  function openModal(event: React.MouseEvent<HTMLAnchorElement>) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    hasOpenedModalRef.current = true;
    setIsOpen(true);
  }

  return (
    <>
      <a ref={triggerRef} className={className ?? "db-button db-button--secondary"} href={fallbackHref} onClick={openModal}>
        {label}
      </a>

      {isOpen ? (
        <div className="saml-widget-modal" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            setIsOpen(false);
          }
        }}>
          <section
            className="saml-widget-modal__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
          >
            <header className="saml-widget-modal__header">
              <div>
                <h2 id={titleId}>{text.title}</h2>
                <p id={descriptionId}>{text.lead}</p>
              </div>
              <button ref={closeRef} className="saml-widget-modal__close" type="button" aria-label={text.close} onClick={() => setIsOpen(false)}>
                X
              </button>
            </header>

            <div
              ref={mountRef}
              className="saml-widget-modal__mount"
              aria-busy={status === "loading" ? "true" : "false"}
            />

            {status === "loading" ? <p className="saml-widget-modal__status" role="status">{text.loading}</p> : null}

            {status === "error" ? <p className="saml-widget-modal__error" role="alert">{text.failed}</p> : null}

            <a className="saml-widget-modal__fallback" href={fallbackHref}>
              {text.fallback}
            </a>
          </section>
        </div>
      ) : null}
    </>
  );
}

function focusWithoutScrolling(element: HTMLElement | null): void {
  if (!element) {
    return;
  }

  try {
    element.focus({ preventScroll: true });
  } catch {
    element.focus();
  }
}

function loadWidgetScript(): Promise<void> {
  if (window.DynamicMailSamlWidget?.mount) {
    return Promise.resolve();
  }
  if (widgetScriptPromise) {
    return widgetScriptPromise;
  }

  widgetScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${widgetLoaderUrl}"]`);
    const script = existing ?? document.createElement("script");

    if (existing?.dataset.dynamicbooksWidgetLoaded === "true") {
      resolve();
      return;
    }

    script.addEventListener("load", () => {
      script.dataset.dynamicbooksWidgetLoaded = "true";
      resolve();
    }, { once: true });
    script.addEventListener("error", () => {
      widgetScriptPromise = null;
      reject(new Error("widget_load_failed"));
    }, { once: true });

    if (!existing) {
      script.src = widgetLoaderUrl;
      script.async = true;
      script.crossOrigin = "anonymous";
      document.head.append(script);
    }
  });

  return widgetScriptPromise;
}

function buildStartUrl(fallbackHref: string): string {
  const url = new URL(fallbackHref, window.location.origin);
  const returnTo = url.searchParams.get("return_to") ?? "/mail/inbox";
  const locale = url.searchParams.get("locale") ?? "sv";
  return `/auth/saml/widget/start?return_to=${encodeURIComponent(returnTo)}&locale=${encodeURIComponent(locale)}`;
}

function resolveMailLandingUrl(data: Record<string, unknown>): string {
  const raw = typeof data.mail_landing_url === "string"
    ? data.mail_landing_url
    : typeof data.redirect_url === "string"
      ? data.redirect_url
      : "";

  try {
    const parsed = new URL(raw);
    if (parsed.protocol === "https:" && parsed.hostname === "mail.dynamicmail.ai" && parsed.pathname === "/admin/inbox") {
      parsed.search = "";
      parsed.hash = "open_modal=dynamicbooks";
      return parsed.toString();
    }
  } catch {
    return mailLandingUrl;
  }

  return mailLandingUrl;
}
